import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { describeLogin } from "../lib/format";
import { VersionTag } from "./VersionTag";
import { FeedbackButton } from "./FeedbackButton";

const NAV = [
  { to: "/", label: "Dashboard", icon: "◧" },
  { to: "/optimizer", label: "Which card?", icon: "◈" },
  { to: "/transactions", label: "Transactions", icon: "☰" },
  { to: "/slips", label: "Charge slips", icon: "▦" },
  { to: "/email", label: "Email alerts", icon: "✉" },
  { to: "/import", label: "Import statements", icon: "⇪" },
  { to: "/cards", label: "Cards & rules", icon: "▤" },
  { to: "/milestones", label: "Milestones", icon: "◎" },
  { to: "/points", label: "Points & redemptions", icon: "★" },
  { to: "/transfers", label: "Miles & transfers", icon: "✈" },
  { to: "/shared", label: "Shared with friends", icon: "⇄" },
  { to: "/settings", label: "Settings", icon: "⚙" },
  { to: "/guide", label: "Getting started", icon: "?" },
  { to: "/feedback", label: "Feedback", icon: "✎" },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // A link from a pop-up (Feedback, say) changes the page without a NavLink
  // click, so the phone menu closes on any change of page.
  useEffect(() => setOpen(false), [location.pathname]);

  // Opening the app is the moment to read any new card alerts. The server
  // skips it when there is no inbox connected or it checked a few minutes ago,
  // and a failure here is shown on the Email alerts page, not as a popup.
  useEffect(() => {
    api.post("/gmail/sync", { auto: true }).catch(() => {});
  }, []);

  return (
    <div className="min-h-full lg:flex">
      {/* Mobile header */}
      <header
        className="sticky top-0 z-30 flex items-center justify-between border-b border-ink-200 bg-white px-4 py-3 lg:hidden"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <span className="font-semibold text-ink-950">Card Points</span>
        <button className="btn-secondary px-3 py-1.5" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? "Close" : "Menu"}
        </button>
      </header>

      <aside
        className={`${open ? "block" : "hidden"} border-b border-ink-200 bg-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:flex-col lg:shrink-0 lg:border-b-0 lg:border-r`}
      >
        <div className="hidden shrink-0 items-center gap-2 px-5 py-5 lg:flex">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent-600 text-sm font-bold text-white">₹</span>
          <div>
            <div className="text-sm font-semibold text-ink-950">Card Points</div>
            <div className="text-xs text-ink-500">Spend & rewards tracker</div>
          </div>
        </div>

        {/* On a short screen the menu scrolls on its own, between the logo and
            the account details, rather than running under them. */}
        <nav className="space-y-0.5 px-3 pb-3 pt-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-accent-50 font-medium text-accent-800"
                    : "text-ink-700 hover:bg-ink-100"
                }`
              }
            >
              <span aria-hidden className="w-4 text-center text-ink-400">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 border-t border-ink-200 px-5 py-4">
          {/* Falling back to the whole email truncates mid-address to something
              unreadable. The part before the @ is what a person recognises,
              and the title carries the address in full. */}
          <div className="truncate text-sm font-medium text-ink-800" title={user?.email ? describeLogin(user.email) : undefined}>
            {user?.name?.trim() || (user?.email ? describeLogin(user.email).split("@")[0] : "Signed in")}
          </div>
          {user?.name?.trim() && user?.email && (
            <div className="truncate text-xs text-ink-500" title={describeLogin(user.email)}>{describeLogin(user.email)}</div>
          )}
          <button onClick={signOut} className="mt-1 text-xs text-ink-500 underline hover:text-ink-700">
            Sign out
          </button>
          <FeedbackButton />
          <VersionTag />
        </div>
      </aside>

      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8" key={location.pathname}>
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>
    </div>
  );
}
